import { useState } from 'react'
import type {
	GroceryBatchCreateResponse,
	GroceryTitle,
	Mode,
	Recipe,
} from '@hewey/shared'
import { api } from '@api'
import { Checkbox } from '@components/Checkbox.tsx'
import { Dialog } from '@components/Dialog.tsx'
import { Toggle } from '@components/Toggle.tsx'
import { useOnline } from '@hooks/useOnline.ts'
import { useMode } from '@mode/ModeContext.tsx'
import { readListChoice, writeListChoice } from './listChoice.ts'
import styles from './AddToGroceriesDialog.module.css'

type Props = {
	recipe: Recipe
	onClose: () => void
	onAdded: (mode: Mode, res: GroceryBatchCreateResponse) => void
}

const LISTS: ReadonlyArray<{ value: Mode; label: string }> = [
	{ value: 'personal', label: 'Personal' },
	{ value: 'family', label: 'Family' },
]

// Ingredients are keyed by section and line, since two sections can share a
// line ("salt").
const keyOf = (section: number, line: number) => `${section}:${line}`

// Puts a recipe's ingredients on a grocery list. Nothing starts ticked, so
// only what's picked goes on, as written.
export const AddToGroceriesDialog = ({ recipe, onClose, onAdded }: Props) => {
	const online = useOnline()
	const { mode: globalMode } = useMode()
	const [list, setList] = useState<Mode>(() => readListChoice() ?? globalMode)
	const [picked, setPicked] = useState<ReadonlySet<string>>(new Set())
	const [busy, setBusy] = useState(false)
	const [failed, setFailed] = useState(false)

	const allKeys = recipe.ingredients.flatMap((sec, si) =>
		sec.items.map((_, i) => keyOf(si, i)),
	)
	const allPicked = picked.size === allKeys.length

	const toggle = (key: string, on: boolean) =>
		setPicked((prev) => {
			const next = new Set(prev)
			if (on) next.add(key)
			else next.delete(key)
			return next
		})

	const add = async () => {
		const titles = recipe.ingredients.flatMap((sec, si) =>
			sec.items
				.filter((_, i) => picked.has(keyOf(si, i)))
				.map((line) => line.slice(0, 200) as GroceryTitle),
		)
		if (titles.length === 0) return
		setBusy(true)
		setFailed(false)
		writeListChoice(list)
		try {
			const res = await api.grocery.batchCreate({ mode: list, titles })
			onAdded(list, res)
		} catch {
			setFailed(true)
			setBusy(false)
		}
	}

	return (
		<Dialog open onClose={onClose}>
			<form
				className={styles.panel}
				onSubmit={(e) => {
					e.preventDefault()
					if (!busy) void add()
				}}
			>
				<div>
					<h2 className={styles.heading}>Add to groceries</h2>
					<p className={styles.subheading}>{recipe.title}</p>
				</div>

				<div className={styles.listRow}>
					<span className={styles.label}>List</span>
					<Toggle
						ariaLabel="Grocery list"
						options={LISTS.map((opt) => ({
							key: opt.value,
							label: opt.label,
							active: list === opt.value,
							onSelect: () => setList(opt.value),
						}))}
					/>
				</div>

				<div className={styles.ingredients}>
					<div className={styles.ingredientsHead}>
						<span className={styles.label}>Ingredients</span>
						<button
							type="button"
							className={styles.selectAll}
							onClick={() =>
								setPicked(new Set(allPicked ? [] : allKeys))
							}
						>
							{allPicked ? 'Select none' : 'Select all'}
						</button>
					</div>
					{recipe.ingredients.map((sec, si) => (
						<div key={si}>
							{sec.heading ? (
								<h3 className={styles.sectionHeading}>
									{sec.heading}
								</h3>
							) : null}
							<ul className={styles.list}>
								{sec.items.map((line, i) => {
									const key = keyOf(si, i)
									return (
										<li key={key}>
											<Checkbox
												className={styles.check}
												checked={picked.has(key)}
												onChange={(on) =>
													toggle(key, on)
												}
											>
												<span>{line}</span>
											</Checkbox>
										</li>
									)
								})}
							</ul>
						</div>
					))}
				</div>

				{failed ? (
					<p className={styles.error}>
						The ingredients could not be added. Check your
						connection and try again.
					</p>
				) : null}

				<div className={styles.actions}>
					<button
						type="button"
						className={styles.secondary}
						onClick={onClose}
						disabled={busy}
					>
						Cancel
					</button>
					<button
						type="submit"
						className={styles.primary}
						disabled={busy || picked.size === 0 || !online}
					>
						{picked.size === 0
							? 'Add'
							: `Add ${picked.size} ${picked.size === 1 ? 'item' : 'items'}`}
					</button>
				</div>
			</form>
		</Dialog>
	)
}
